"use client";

/**
 * Loads the public-facing activity + slots for a single activity ID, pairing them with the
 * per-slot registration counts prefetched event-wide by PublicSignupCountsProvider (so the
 * counts are available immediately, without a per-open fetch). Returns a `refresh()` callback
 * used after a successful signup/waitlist join so the parent UI reflects new server state.
 *
 * Used by ScheduleDetailModal (and any future surface that needs to display the
 * full activity-signup summary for a single activity).
 */

import { useCallback, useEffect, useState } from "react";
import { usePublicSignupCounts } from "../_components/public/PublicSignupCountsProvider";
import { getActivityPublic, listTimeSlotsPublic } from "../_logic/actions/public-actions";
import type { SlotRegistrationCounts } from "../_logic/actions/signup-actions";
import type { ActivityRow, ActivityTimeSlotRow } from "../_logic/types/db-types";

export interface UseLinkedActivityResult {
  activity: ActivityRow | null;
  slots: ActivityTimeSlotRow[];
  slotCounts: Record<number, SlotRegistrationCounts>;
  isLoading: boolean;
  /** True when the initial fetch failed (after the load attempt resolved). */
  hasError: boolean;
  /** Re-fetches activity + slots + counts. Returns the same triple shape. */
  refresh: () => void;
}

/**
 * @param activityId - Activity to load, or `null` when there's no linked activity (returns inert state)
 * @param enabled - When false (e.g., not in public view), skips the fetch and returns inert state
 * @returns Loaded activity, slots, counts, and a `refresh()` callback
 */
export function useLinkedActivity(activityId: number | null, enabled: boolean): UseLinkedActivityResult {
  // Per-slot counts come from the shared prefetch; the slots/activity are still loaded on demand.
  const signupCountsData = usePublicSignupCounts();
  const [activity, setActivity] = useState<ActivityRow | null>(null);
  const [slots, setSlots] = useState<ActivityTimeSlotRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const load = useCallback(
    (signal: { cancelled: boolean }) => {
      if (!enabled || activityId == null) {
        setActivity(null);
        setSlots([]);
        setHasError(false);
        return;
      }
      setIsLoading(true);
      setHasError(false);
      Promise.all([getActivityPublic(activityId), listTimeSlotsPublic(activityId)])
        .then(([a, s]) => {
          if (signal.cancelled) return;
          setActivity(a);
          setSlots(s);
        })
        .catch((err) => {
          if (signal.cancelled) return;
          console.error("useLinkedActivity: failed to load activity", { activityId }, err);
          setHasError(true);
        })
        .finally(() => {
          if (!signal.cancelled) setIsLoading(false);
        });
    },
    [activityId, enabled]
  );

  useEffect(() => {
    const signal = { cancelled: false };
    load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  const refresh = useCallback(() => {
    // Refresh the activity/slots; counts re-sync via the shared provider on the registrationsVersion
    // bump the caller fires alongside this (see ScheduleDetailModal.handleSignupSuccess).
    const signal = { cancelled: false };
    load(signal);
    signupCountsData?.refresh();
  }, [load, signupCountsData]);

  return { activity, slots, slotCounts: signupCountsData?.slotCounts ?? {}, isLoading, hasError, refresh };
}
